import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Navigate } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { LiveRoom } from "@/components/LiveRoom";
import { LiveStudio } from "@/components/LiveStudio";
import { FeedSkeleton } from "@/components/FeedSkeleton";
import { DonateModal } from "@/components/DonateModal";
import { donationsAvailable } from "@/lib/payments";
import { toast } from "sonner";
import { useSeo, PUBLIC_ORIGIN } from "@/lib/seo";

/**
 * The dedicated Live browse page has been removed — live rediffusions now appear
 * as standard video posts in the general feed. This route is only a thin launcher:
 *   • /live?create=1        → opens the broadcast studio (LiveStudio)
 *   • /live?watch={live_id} → watches an ongoing broadcast (LiveRoom)
 * Anything else redirects to the feed.
 */
export const Live = () => {
  const { user, t, config } = useApp();
  const [searchParams] = useSearchParams();
  const nav = useNavigate();

  const create = searchParams.get("create") === "1";
  const watchId = searchParams.get("watch");
  const churchParam = searchParams.get("church") || "";
  const titleParam = searchParams.get("title") || "";
  const resumeId = searchParams.get("resume") || "";
  const scheduledId = searchParams.get("scheduled") || "";

  const [watching, setWatching] = useState(null);
  const studioOpen = create || !!resumeId || !!scheduledId;
  const [donateFor, setDonateFor] = useState(null);
  const [donateLive, setDonateLive] = useState(null);
  const [loading, setLoading] = useState(!!watchId);

  // Lien /live?watch=<id> partagé (WhatsApp, Facebook…) : aperçu du direct lui-même (titre, hôte, vignette), plus la
  // vitrine générique. Même carte image que /api/e/live/<id>.
  useSeo(watching ? {
    title: `${watching.title || "Direct"} — ${watching.host?.name ? `${watching.host.name} ` : ""}en direct sur Yalola`,
    description: `${watching.host?.name ? `${watching.host.name} · ` : ""}Rejoins le direct sur Yalola 🔴`,
    canonical: `/live?watch=${watching.id}`, url: `/live?watch=${watching.id}`, type: "video.other",
    image: `${PUBLIC_ORIGIN}/api/e/live/${watching.id}/share.jpg`,
  } : null, [watching?.id]);

  useEffect(() => {
    if (!watchId) return;
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/live/${watchId}`);
        if (cancel) return;
        if (data.live && data.live.status !== "ended") setWatching(data.live);
        else { toast.info(t("live.ended_msg", "Ce direct est terminé.")); nav("/home"); }
      } catch {
        if (!cancel) { toast.error(t("live.not_found", "Direct introuvable")); nav("/home"); }
      }
      if (!cancel) setLoading(false);
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchId]);

  if (!create && !watchId && !resumeId && !scheduledId) return <Navigate to="/home" replace />;
  if ((create || resumeId || scheduledId) && !user) return <Navigate to="/?tab=login" replace />;

  // Only the ACTUAL author of a live re-enters it as HOST (broadcaster controls).
  // EXCEPTION: a browser (WHIP/WebRTC) live is broadcast from the LiveStudio on the
  // author's *broadcasting* device. If the author opens the public watch link on a
  // SECOND device (or PC), they must be a plain VIEWER — never a second host — otherwise
  // they hijack the host slot (viewer count freezes at 0 on the phone) and get a
  // conflicting "Terminer" button. Author-as-host stays only for OBS/RTMPS lives,
  // which have no LiveStudio and are monitored/ended from this watch page.
  const isHostRole = !!watching && watching.host_id === user?.id && watching.ingest !== "webrtc";

  return (
    <AppShell showSidebars={!watching}>
      {loading && <div data-testid="live-loading"><FeedSkeleton count={1} /></div>}
      {watching && !studioOpen && (
        <LiveRoom
          live={watching}
          role={isHostRole ? "host" : "viewer"}
          onClose={() => { setWatching(null); nav("/home"); }}
          onDonate={(!isHostRole && watching.accept_offerings && donationsAvailable(config?.payments, config?.settings?.features)) ? () => { setDonateFor(watching.host); setDonateLive(watching.id); } : undefined}
        />
      )}
      <DonateModal
        open={!!donateFor}
        onOpenChange={(v) => { if (!v) { setDonateFor(null); setDonateLive(null); } }}
        receiver={donateFor}
        liveId={donateLive}
      />
      {studioOpen && <LiveStudio user={user} initialChurchId={churchParam} initialTitle={titleParam} resumeLiveId={resumeId} scheduledId={scheduledId} onClose={() => nav("/home")} />}
    </AppShell>
  );
};

export default Live;
